import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { AuthWrapper } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useAuthForm } from "../hooks/useAuthForm"
import { type ForgotPasswordFormData, forgotPasswordSchema } from "../schemas/auth.schema"
import { forgotPassword } from "../services/auth.service"

export function ForgotPasswordPage() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const [formState, setFormState] = useState<"default" | "success">("default")
  const [storedEmail, setStoredEmail] = useState("")
  const [isResending, setIsResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return
    const interval = setInterval(() => {
      setResendCooldown(prev => Math.max(0, prev - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [resendCooldown])

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    forgotPasswordSchema,
    async (data: ForgotPasswordFormData) => {
      await forgotPassword(data.email)
      setStoredEmail(data.email)
      setFormState("success")
    },
  )

  const handleResend = async () => {
    if (storedEmail && resendCooldown === 0) {
      setIsResending(true)
      try {
        await forgotPassword(storedEmail)
        setResendCooldown(60)
      } finally {
        setIsResending(false)
      }
    }
  }

  const emailRegister = form.register("email")

  if (formState === "success") {
    return (
      <AuthWrapper>
        <div className="space-y-4 px-5 sm:px-10 md:px-20">
          <div className="text-center">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("forgotPassword.successTitle")}
            </h2>
          </div>

          <div className="space-y-4 text-center">
            <p className="text-primary text-sm">{t("forgotPassword.successDescription")}</p>
          </div>

          <div className="text-center py-2 text-sm">
            {t("forgotPassword.resendHint")}{" "}
            <Button
              type="button"
              variant="link"
              size="default"
              className="text-sm p-0 h-auto font-normal"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              isLoading={isResending}
            >
              {resendCooldown > 0
                ? t("forgotPassword.requestAgainCooldown", { seconds: resendCooldown })
                : t("forgotPassword.requestAgain")}
            </Button>
          </div>

          <div className="py-2 space-y-4 flex justify-center">
            <Button
              type="button"
              variant="primary-white"
              size="md"
              className="uppercase max-w-[200px]"
              fullWidth
              onClick={() => navigate("/login", { replace: true })}
            >
              {t("forgotPassword.doneButton")}
            </Button>
          </div>
        </div>
      </AuthWrapper>
    )
  }

  return (
    <AuthWrapper>
      <form onSubmit={handleSubmit}>
        <div className="space-y-6 px-5 sm:px-10 md:px-15">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-[500] text-primary font-base">
              {t("forgotPassword.title")}
            </h2>
            <p className="text-primary text-[13px] mt-2">{t("forgotPassword.description")}</p>
          </div>

          <div className="space-y-6">
            <CustomInput
              label={t("forgotPassword.emailLabel")}
              type="email"
              placeholder={t("forgotPassword.emailPlaceholder")}
              required={true}
              {...emailRegister}
              error={(form.formState.errors.email?.message || serverError) as string | undefined}
              disabled={isSubmitting}
              onChange={e => {
                emailRegister.onChange?.(e)
                clearServerError()
              }}
            />

            <div className="py-2 flex justify-center">
              <Button
                type="submit"
                variant="primary-white"
                size="md"
                className="uppercase max-w-[200px]"
                isLoading={isSubmitting}
                fullWidth
              >
                {t("forgotPassword.submitButton")}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </AuthWrapper>
  )
}
